import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { placementAPI } from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import {
  ArrowLeft, LogOut, Briefcase, Building2,
  IndianRupee, Clock, Star, Loader2, CheckCircle
} from 'lucide-react';

const STATUS_CONFIG = {
  ACTIVE:     { label: 'Active',     color: 'bg-green-100 text-green-700'  },
  COMPLETED:  { label: 'Completed',  color: 'bg-blue-100 text-blue-700'    },
  TERMINATED: { label: 'Terminated', color: 'bg-red-100 text-red-600'      },
};

function StarRating({ value, onChange, readOnly }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button"
          onClick={() => !readOnly && onChange && onChange(n)}
          className={`transition ${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}>
          <Star className={`h-5 w-5 ${n <= value ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
        </button>
      ))}
    </div>
  );
}

export default function MyPlacements() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingModal, setRatingModal] = useState(null);
  const [rating, setRating] = useState(0);
  const [ratingText, setRatingText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await placementAPI.myPlacements();
      setPlacements(res.data.data || []);
    } catch { toast.error('Failed to load placements'); }
    finally { setLoading(false); }
  };

  const handleRateCompany = async () => {
    if (!rating) { toast.error('Please select a rating'); return; }
    setSubmitting(true);
    try {
      await placementAPI.rateCompany(ratingModal, { rating, ratingText: ratingText || null });
      toast.success('Rating submitted!');
      setRatingModal(null); setRating(0); setRatingText('');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
    </div>
  );

  const active = placements.filter(p => p.status === 'ACTIVE');
  const past = placements.filter(p => p.status !== 'ACTIVE');

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-7 w-auto" />
            <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded-lg text-xs font-bold uppercase">Staff</span>
          </div>
          <button onClick={() => { logout(); navigate('/login'); }}
            className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-slate-800">My Placements</h1>
          <p className="text-sm text-gray-400 mt-0.5">Your current and past work engagements</p>
        </div>

        {placements.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <Briefcase className="h-14 w-14 text-gray-200 mx-auto mb-4" />
            <p className="text-gray-400 font-semibold">No placements yet</p>
            <p className="text-sm text-gray-400 mt-1">Apply to opportunities or accept hire requests to get started</p>
          </div>
        ) : (
          <div className="space-y-6">
            {active.length > 0 && (
              <div>
                <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
                  Current Placement
                </h2>
                {active.map(p => <PlacementCard key={p.id} placement={p} onRate={() => setRatingModal(p.id)} />)}
              </div>
            )}
            {past.length > 0 && (
              <div>
                <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
                  Past Placements
                </h2>
                {past.map(p => (
                  <PlacementCard key={p.id} placement={p}
                    onRate={!p.ratingByStaff ? () => setRatingModal(p.id) : null} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rate company modal */}
      {ratingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
            <h3 className="font-bold text-slate-800 mb-1">Rate This Company</h3>
            <p className="text-sm text-gray-400 mb-5">
              Your rating helps other staff make informed decisions.
            </p>
            <div className="mb-4">
              <label className="block text-xs font-bold text-slate-700 mb-2">Rating *</label>
              <StarRating value={rating} onChange={setRating} />
            </div>
            <div className="mb-5">
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Review <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea value={ratingText} onChange={e => setRatingText(e.target.value)}
                placeholder="Share your experience working with this company..."
                rows={3}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setRatingModal(null); setRating(0); setRatingText(''); }}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl font-semibold text-sm text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleRateCompany} disabled={submitting || !rating}
                className="flex-1 py-2.5 bg-teal-600 text-white rounded-xl font-bold text-sm hover:bg-teal-700 disabled:opacity-60">
                {submitting ? 'Submitting...' : 'Submit Rating'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PlacementCard({ placement: p, onRate }) {
  const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.ACTIVE;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-4">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-bold text-slate-800">{p.companyName}</h3>
            <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded font-medium">
              {p.organizationType}
            </span>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.color}`}>
              {cfg.label}
            </span>
          </div>
          <p className="text-sm text-teal-600 font-medium">{p.roleName}</p>
        </div>
        <span className="text-xs px-2.5 py-1 bg-gray-100 text-gray-600 rounded-lg font-semibold shrink-0">
          {p.source === 'OPPORTUNITY' ? 'Via Opportunity' : 'Direct Hire'}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        {p.dailyRateAgreed && (
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs text-gray-400 mb-0.5">Daily Rate</p>
            <p className="font-bold text-slate-800">₹{p.dailyRateAgreed}</p>
          </div>
        )}
        {p.shiftHours && (
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs text-gray-400 mb-0.5">Shift</p>
            <p className="font-bold text-slate-800">{p.shiftHours}hr</p>
          </div>
        )}
        {p.startDate && (
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs text-gray-400 mb-0.5">Start Date</p>
            <p className="font-bold text-slate-800 text-sm">
              {new Date(p.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
          </div>
        )}
      </div>

      {/* Company rating of staff */}
      {p.ratingByCompany && (
        <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-3 mb-3">
          <p className="text-xs font-bold text-yellow-800 mb-1">Company rated you</p>
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5">
              {[1,2,3,4,5].map(n => (
                <Star key={n} className={`h-4 w-4 ${n <= p.ratingByCompany ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
              ))}
            </div>
            {p.ratingByCompanyText && <p className="text-xs text-yellow-700">"{p.ratingByCompanyText}"</p>}
          </div>
        </div>
      )}

      {/* Staff rate company */}
      {p.status === 'COMPLETED' && (
        p.ratingByStaff ? (
          <div className="flex items-center gap-2 text-xs text-green-600">
            <CheckCircle className="h-4 w-4" />
            You rated this company {p.ratingByStaff}/5
          </div>
        ) : onRate && (
          <button onClick={onRate}
            className="flex items-center gap-1.5 text-sm font-bold text-teal-600 hover:text-teal-700">
            <Star className="h-4 w-4" /> Rate this company
          </button>
        )
      )}
    </div>
  );
}
